import React, { useState, useEffect } from "react";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
	collection,
	addDoc,
	getDocs,
	deleteDoc,
	doc,
	updateDoc,
} from "firebase/firestore";
import { db } from "../firebase-config";
import "./NewBlogPost.css"; // We can reuse the blog post styles
import { useNavigate } from "react-router-dom";

function NewNewsletter() {
	const navigate = useNavigate();
	const [title, setTitle] = useState("");
	const [excerpt, setExcerpt] = useState("");
	const [content, setContent] = useState("");
	const [image, setImage] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [mode, setMode] = useState("create");
	const [newsletters, setNewsletters] = useState([]);
	const [selectedNewsletter, setSelectedNewsletter] = useState(null);

	const handleImageChange = (e) => {
		if (e.target.files[0]) {
			setImage(e.target.files[0]);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError("");

		try {
			let imageUrl = "/images/services-home-bg.jpeg";

			if (image) {
				const storage = getStorage();
				const storageRef = ref(
					storage,
					`newsletter-images/${Date.now()}-${image.name}`
				);

				const metadata = {
					contentType: image.type,
					customMetadata: {
						"Access-Control-Allow-Origin": "*",
					},
				};

				await uploadBytes(storageRef, image, metadata);
				imageUrl = await getDownloadURL(storageRef);
			}

			const newsletter = {
				title,
				excerpt,
				content,
				imageUrl,
				author: "Sibling Kinect",
				date: new Date().toISOString(),
				readTime: `${Math.ceil(content.split(" ").length / 200)} min read`,
				createdAt: new Date(),
			};

			await addDoc(collection(db, "newsletters"), newsletter);

			alert("Newsletter created successfully!");
			navigate("/newsletter");
		} catch (error) {
			console.error("Error creating newsletter:", error);
			setError("Error creating newsletter. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const fetchNewsletters = async () => {
		try {
			const querySnapshot = await getDocs(collection(db, "newsletters"));
			const newslettersList = querySnapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));
			setNewsletters(newslettersList);
		} catch (error) {
			console.error("Error fetching newsletters:", error);
			setError("Error fetching newsletters. Please try again.");
		}
	};

	const handleDelete = async (newsletterId) => {
		if (window.confirm("Are you sure you want to delete this newsletter?")) {
			try {
				await deleteDoc(doc(db, "newsletters", newsletterId));
				alert("Newsletter deleted successfully!");
				fetchNewsletters();
			} catch (error) {
				console.error("Error deleting newsletter:", error);
				setError("Error deleting newsletter. Please try again.");
			}
		}
	};

	const handleEditSelect = (newsletter) => {
		setSelectedNewsletter(newsletter);
		setTitle(newsletter.title);
		setExcerpt(newsletter.excerpt);
		setContent(newsletter.content);
		setMode("edit");
	};

	const handleUpdate = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError("");

		try {
			let imageUrl = selectedNewsletter.imageUrl;

			if (image) {
				const storage = getStorage();
				const storageRef = ref(
					storage,
					`newsletter-images/${Date.now()}-${image.name}`
				);
				const metadata = {
					contentType: image.type,
					customMetadata: {
						"Access-Control-Allow-Origin": "*",
					},
				};

				await uploadBytes(storageRef, image, metadata);
				imageUrl = await getDownloadURL(storageRef);
			}

			const updatedNewsletter = {
				title,
				excerpt,
				content,
				imageUrl,
				updatedAt: new Date(),
				readTime: `${Math.ceil(content.split(" ").length / 200)} min read`,
			};

			await updateDoc(
				doc(db, "newsletters", selectedNewsletter.id),
				updatedNewsletter
			);
			alert("Newsletter updated successfully!");
			setMode("delete");
			setSelectedNewsletter(null);
			clearForm();
			fetchNewsletters();
		} catch (error) {
			console.error("Error updating newsletter:", error);
			setError("Error updating newsletter. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const clearForm = () => {
		setTitle("");
		setExcerpt("");
		setContent("");
		setImage(null);
	};

	useEffect(() => {
		if (mode === "delete") {
			fetchNewsletters();
		}
	}, [mode]);

	return (
		<div className="new-blog-post">
			<div className="mode-toggle">
				<button
					className={`toggle-btn ${mode === "create" ? "active" : ""}`}
					onClick={() => {
						setMode("create");
						clearForm();
						setSelectedNewsletter(null);
					}}
				>
					Create Mode
				</button>
				<button
					className={`toggle-btn ${mode === "delete" ? "active" : ""}`}
					onClick={() => {
						setMode("delete");
						clearForm();
						setSelectedNewsletter(null);
					}}
				>
					Edit Mode
				</button>
			</div>

			{mode === "create" ? (
				<>
					<h2>Create New Newsletter</h2>
					{error && <div className="error-message">{error}</div>}
					<form onSubmit={handleSubmit}>
						<div className="form-group">
							<label>Title</label>
							<input
								type="text"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								required
							/>
						</div>

						<div className="form-group">
							<label>Excerpt</label>
							<textarea
								value={excerpt}
								onChange={(e) => setExcerpt(e.target.value)}
								required
								rows="3"
								placeholder="Brief summary of your newsletter..."
							/>
						</div>

						<div className="form-group">
							<label>Content</label>
							<textarea
								value={content}
								onChange={(e) => setContent(e.target.value)}
								required
								rows="10"
								placeholder="Write your newsletter content here..."
							/>
						</div>

						<div className="form-group">
							<label>Featured Image</label>
							<input
								type="file"
								onChange={handleImageChange}
								accept="image/*"
								className="file-input"
							/>
						</div>

						<button type="submit" disabled={loading} className="submit-button">
							{loading ? "Creating..." : "Create Newsletter"}
						</button>
					</form>
				</>
			) : mode === "edit" ? (
				<>
					<h2>Edit Newsletter</h2>
					{error && <div className="error-message">{error}</div>}
					<form onSubmit={handleUpdate}>
						<div className="form-group">
							<label>Title</label>
							<input
								type="text"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								required
							/>
						</div>

						<div className="form-group">
							<label>Excerpt</label>
							<textarea
								value={excerpt}
								onChange={(e) => setExcerpt(e.target.value)}
								required
								rows="3"
								placeholder="Brief summary of your newsletter..."
							/>
						</div>

						<div className="form-group">
							<label>Content</label>
							<textarea
								value={content}
								onChange={(e) => setContent(e.target.value)}
								required
								rows="10"
								placeholder="Write your newsletter content here..."
							/>
						</div>

						<div className="form-group">
							<label>Featured Image</label>
							<input
								type="file"
								onChange={handleImageChange}
								accept="image/*"
								className="file-input"
							/>
							{selectedNewsletter.imageUrl && (
								<img
									src={selectedNewsletter.imageUrl}
									alt={selectedNewsletter.title}
									className="current-image-preview"
								/>
							)}
						</div>
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<button
								type="submit"
								disabled={loading}
								className="submit-button"
							>
								{loading ? "Updating..." : "Update Newsletter"}
							</button>
							<button
								type="button"
								onClick={() => {
									setMode("delete");
									clearForm();
									setSelectedNewsletter(null);
								}}
								className="cancel-button"
							>
								Cancel
							</button>
						</div>
					</form>
				</>
			) : (
				<>
					<h2>Manage Newsletters</h2>
					{error && <div className="error-message">{error}</div>}
					<div className="newsletters-list">
						{newsletters.map((newsletter) => (
							<div key={newsletter.id} className="newsletter-item">
								<h3>{newsletter.title}</h3>
								<p>{newsletter.excerpt}</p>
								<p className="newsletter-meta">
									{new Date(newsletter.date).toLocaleDateString()} •{" "}
									{newsletter.readTime}
								</p>
								<div className="newsletter-actions">
									<button
										onClick={() => handleEditSelect(newsletter)}
										className="edit-button"
									>
										Edit
									</button>
									<button
										onClick={() => handleDelete(newsletter.id)}
										className="delete-button"
									>
										Delete
									</button>
								</div>
							</div>
						))}
					</div>
				</>
			)}
		</div>
	);
}

export default NewNewsletter;
